import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import type { ApexOptions } from "apexcharts";
import SafeApexChart from "../../../Components/Common/SafeApexChart";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  persistTenantSession,
} from "../../../helpers/api_helper";
import { intlLocaleFromI18n } from "../../../helpers/money";
import {
  formatSaRate,
  getSaActivityLogs,
  getSaTenant,
  impersonateSaTenant,
  patchSaTenant,
  patchSaTenantSendingRamp,
  recreateSaSesIdentity,
  resetSaEmailCircuit,
  resetSaOwnerMfa,
  resendSaOwnerInvite,
  resumeSaSend,
  SaActivityLog,
  SaTenantHealth,
} from "../../../helpers/super_admin_ops_helper";
import {
  getSuperAdminPlans,
  SaPlan,
} from "../../../helpers/super_admin_plans_helper";
import {
  formatNextUtcDayResetLocal,
  tenantTimezoneDisplayLabel,
} from "../../../helpers/tenantTimezone";
import SesByoCard from "./SesByoCard";

type ActionModal =
  | "suspend"
  | "reactivate"
  | "impersonate"
  | "resetCircuit"
  | "channelSelfServe"
  | "edit"
  | "editRamp"
  | "confirmTrustedRamp"
  | "resetOwnerMfa"
  | null;

type SendingStageValue = "new" | "warming" | "trusted";

/**
 * Ficha de saúde do tenant + ações ops (LAC-042 · DOC 02 § 6.2 · LAC-088).
 * Por quê botões aqui: lista só sinaliza; decisão de suspender/entrar
 * precisa do contexto de cota/circuit da ficha.
 */
const SuperAdminTenantDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tenantId = Number(id);
  const locale = intlLocaleFromI18n(i18n.language);
  const highlightRamp = searchParams.get("highlight") === "ramp";
  const rampCardRef = useRef<HTMLDivElement | null>(null);

  const [tenant, setTenant] = useState<SaTenantHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionModal, setActionModal] = useState<ActionModal>(null);
  const [blockReason, setBlockReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [channelDraft, setChannelDraft] = useState<string>("self_serve");
  const [channelBusy, setChannelBusy] = useState(false);
  const [dnsModeDraft, setDnsModeDraft] = useState<"cname" | "ns">("cname");
  const [dnsModeBusy, setDnsModeBusy] = useState(false);
  const [plans, setPlans] = useState<SaPlan[]>([]);
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editFieldErrors, setEditFieldErrors] = useState<
    Record<string, string>
  >({});
  const [editName, setEditName] = useState("");
  const [editDocument, setEditDocument] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editOwnerName, setEditOwnerName] = useState("");
  const [editOwnerEmail, setEditOwnerEmail] = useState("");
  const [editOwnerPassword, setEditOwnerPassword] = useState("");
  const [editShowPassword, setEditShowPassword] = useState(false);
  const [editPlanId, setEditPlanId] = useState<number | "">("");
  const [editBillingChannel, setEditBillingChannel] = useState<
    "self_serve" | "brdominio"
  >("self_serve");
  const [editLocale, setEditLocale] = useState("pt-BR");
  const [editTimezone, setEditTimezone] = useState("America/Sao_Paulo");
  const [rampBusy, setRampBusy] = useState(false);
  const [rampError, setRampError] = useState<string | null>(null);
  const [rampStage, setRampStage] = useState<SendingStageValue>("new");
  const [rampCapInput, setRampCapInput] = useState("");
  const [rampMinIntervalInput, setRampMinIntervalInput] = useState("0");
  const [rampLogs, setRampLogs] = useState<SaActivityLog[]>([]);
  /** Checklist playbook no modal de reset (LAC-141) — 4 itens ou nota curta. */
  const [playbookChecks, setPlaybookChecks] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [playbookNote, setPlaybookNote] = useState("");
  const [resumingSendId, setResumingSendId] = useState<number | null>(null);
  const [recreatingSes, setRecreatingSes] = useState(false);
  const [recreateSesForce, setRecreateSesForce] = useState(false);
  const [resendInviteBusy, setResendInviteBusy] = useState(false);
  /** Anti-viés: URL do último create/resend (token só nessa resposta). */
  const [inviteAcceptUrl, setInviteAcceptUrl] = useState<string | null>(null);
  /** Cooldown visual 60s após reenvio com e-mail (além do throttle API). */
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  /** LAC-189 polish: recovery|disable + códigos 1× + cooldown pós-ação. */
  const [mfaResetMode, setMfaResetMode] = useState<"recovery" | "disable">(
    "recovery"
  );
  const [mfaRecoveryCodes, setMfaRecoveryCodes] = useState<string[] | null>(
    null
  );
  // Evita fechar o modal sem o SA confirmar que já passou os códigos ao cliente.
  const [mfaCodesSentConfirmed, setMfaCodesSentConfirmed] = useState(false);
  const [mfaResetCooldownSec, setMfaResetCooldownSec] = useState(0);
  const inviteBannerRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    if (!Number.isFinite(tenantId) || tenantId < 1) {
      setError(t("sa.tenants.error.loadOne"));
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getSaTenant(tenantId);
      if (!res?.success || !res.data) {
        setError(t("sa.tenants.error.loadOne"));
        setTenant(null);
        return;
      }
      setTenant(res.data);
      setChannelDraft(res.data.billing_channel || "self_serve");
      setDnsModeDraft(
        res.data.dns_delegation_mode === "ns" ? "ns" : "cname"
      );
      document.title = t("sa.tenants.detail.documentTitle", {
        name: res.data.name,
      });
    } catch (e: any) {
      setError(e?.message || t("sa.tenants.error.loadOne"));
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!Number.isFinite(tenantId) || tenantId < 1) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getSaActivityLogs({
          tenant_id: tenantId,
          action: "tenant.ramp_updated",
          per_page: 3,
          page: 1,
        });
        if (!cancelled && res?.success) {
          setRampLogs(res.data || []);
        }
      } catch {
        if (!cancelled) {
          setRampLogs([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, tenant?.sending_stage, tenant?.daily_send_cap]);

  useEffect(() => {
    if (!highlightRamp || !tenant || loading) {
      return;
    }
    rampCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [highlightRamp, tenant, loading]);

  const highlightInvite = searchParams.get("highlight") === "invite";
  const sparkDateRaw = searchParams.get("spark_date") || "";
  const sparkHighlightDate =
    /^\d{4}-\d{2}-\d{2}$/.test(sparkDateRaw) ? sparkDateRaw : "";
  const sparkCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!highlightInvite || !tenant?.owner_invite?.pending || loading) {
      return;
    }
    inviteBannerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [highlightInvite, tenant?.owner_invite?.pending, loading]);

  // LAC-142 polish: voltar de Envios com ?spark_date= → scroll + marca no gráfico.
  useEffect(() => {
    if (!sparkHighlightDate || !tenant || loading) {
      return;
    }
    sparkCardRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [sparkHighlightDate, tenant, loading]);

  useEffect(() => {
    if (resendCooldownSec <= 0) {
      return;
    }
    const timer = window.setTimeout(
      () => setResendCooldownSec((s) => Math.max(0, s - 1)),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [resendCooldownSec]);

  useEffect(() => {
    if (mfaResetCooldownSec <= 0) {
      return;
    }
    const timer = window.setTimeout(
      () => setMfaResetCooldownSec((s) => Math.max(0, s - 1)),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [mfaResetCooldownSec]);

  const closeModal = () => {
    if (busy || channelBusy || editBusy || rampBusy) {
      return;
    }
    if (actionModal === "channelSelfServe") {
      setChannelDraft(tenant?.billing_channel || "self_serve");
    }
    setActionModal(null);
    setBlockReason("");
    setReasonError(null);
    setEditError(null);
    setEditFieldErrors({});
    setEditOwnerPassword("");
    setEditShowPassword(false);
    setRampError(null);
    setPlaybookChecks([false, false, false, false]);
    setPlaybookNote("");
    setMfaRecoveryCodes(null);
    setMfaResetMode("recovery");
    setMfaCodesSentConfirmed(false);
  };

  const openResetCircuitModal = () => {
    setPlaybookChecks([false, false, false, false]);
    setPlaybookNote("");
    setActionModal("resetCircuit");
  };

  // LAC-192: reenviar link de definir senha (novo token).
  const onResendOwnerInvite = async (sendEmail = true) => {
    if (!tenant) return;
    if (
      sendEmail &&
      !window.confirm(
        t("sa.tenants.detail.ownerInvite.confirm", {
          email: tenant.owner_invite?.email || tenant.owner?.email || "",
        })
      )
    ) {
      return;
    }
    setResendInviteBusy(true);
    try {
      const res = await resendSaOwnerInvite(tenant.id, {
        send_email: sendEmail,
      });
      if (!res?.success || !res.data) {
        toast.error(t("sa.tenants.detail.ownerInvite.error"));
        return;
      }
      setTenant(res.data);
      if (res.data.invite_accept_url) {
        setInviteAcceptUrl(res.data.invite_accept_url);
      }
      if (sendEmail) {
        setResendCooldownSec(60);
      }
      if (res.data.invite_mail?.failed) {
        toast.warning(
          res.message || t("sa.tenants.detail.ownerInvite.mailFailed"),
          { autoClose: 5000 }
        );
      } else {
        toast.success(
          res.message ||
            (sendEmail
              ? t("sa.tenants.detail.ownerInvite.success")
              : t("sa.tenants.detail.ownerInvite.linkReady")),
          { autoClose: 2500 }
        );
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.ownerInvite.error"));
    } finally {
      setResendInviteBusy(false);
    }
  };

  const copyInviteLink = async () => {
    if (!inviteAcceptUrl) {
      toast.info(t("sa.tenants.detail.ownerInvite.copyNeedGenerate"));
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteAcceptUrl);
      toast.success(t("sa.tenants.detail.ownerInvite.copied"), {
        autoClose: 1500,
      });
    } catch {
      toast.error(t("sa.tenants.detail.ownerInvite.copyFailed"));
    }
  };

  // LAC-189: recovery one-shot (padrão) ou desliga MFA — sem o 2º fator.
  const runResetOwnerMfa = async () => {
    if (!tenant) return;
    setBusy(true);
    try {
      const res = await resetSaOwnerMfa(tenant.id, mfaResetMode);
      if (!res?.success || !res.data) {
        toast.error(t("sa.tenants.detail.ownerMfa.error"));
        return;
      }
      const codes = res.data.mfa_reset?.recovery_codes;
      setTenant(res.data);
      setMfaResetCooldownSec(60);
      if (mfaResetMode === "recovery" && codes?.length) {
        setMfaRecoveryCodes(codes);
        toast.success(
          res.message || t("sa.tenants.detail.ownerMfa.recoverySuccess"),
          { autoClose: 4000 }
        );
      } else {
        setMfaRecoveryCodes(null);
        setActionModal(null);
        toast.success(
          res.message || t("sa.tenants.detail.ownerMfa.success"),
          { autoClose: 3500 }
        );
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.ownerMfa.error"));
    } finally {
      setBusy(false);
    }
  };

  const copyMfaRecoveryCodes = async () => {
    if (!mfaRecoveryCodes?.length) return;
    try {
      await navigator.clipboard.writeText(mfaRecoveryCodes.join("\n"));
      toast.success(t("sa.tenants.detail.ownerMfa.copied"), {
        autoClose: 1500,
      });
    } catch {
      toast.error(t("sa.tenants.detail.ownerMfa.copyFailed"));
    }
  };

  const playbookConfirmed =
    playbookChecks.every(Boolean) || playbookNote.trim().length >= 8;

  const runResumeSend = async (sendId: number) => {
    setResumingSendId(sendId);
    try {
      const res = await resumeSaSend(sendId);
      toast.success(
        res?.message || t("sa.tenants.detail.awaitingSends.resumeSuccess"),
        { autoClose: 2500 }
      );
      await load();
    } catch (e: any) {
      toast.error(
        e?.message || t("sa.tenants.detail.awaitingSends.resumeError"),
        { autoClose: 4000 }
      );
    } finally {
      setResumingSendId(null);
    }
  };

  const runRecreateSes = async () => {
    setRecreatingSes(true);
    try {
      const res = await recreateSaSesIdentity(tenantId, recreateSesForce);
      toast.success(
        res?.message || t("sa.tenants.detail.sesRecreate.success"),
        { autoClose: 3000 }
      );
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(
        e?.message || t("sa.tenants.detail.sesRecreate.error"),
        { autoClose: 4000 }
      );
    } finally {
      setRecreatingSes(false);
    }
  };

  const openRampModal = () => {
    if (!tenant) return;
    const stage = (tenant.sending_stage || "new") as SendingStageValue;
    setRampStage(
      stage === "warming" || stage === "trusted" ? stage : "new"
    );
    setRampCapInput(
      tenant.daily_send_cap != null ? String(tenant.daily_send_cap) : ""
    );
    setRampMinIntervalInput(
      String(tenant.min_send_interval_seconds ?? 0)
    );
    setRampError(null);
    setActionModal("editRamp");
  };

  const runLiberarLimiteDiario = async () => {
    setRampBusy(true);
    setRampError(null);
    try {
      const res = await patchSaTenantSendingRamp(tenantId, {
        sending_stage: "trusted",
        daily_send_cap: null,
      });
      toast.success(res?.message || t("sa.tenants.ramp.liberarSuccess"), {
        autoClose: 2500,
      });
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
      setActionModal(null);
    } catch (e: any) {
      const msg = e?.message || t("sa.tenants.detail.actions.error");
      setRampError(msg);
      toast.error(msg, { autoClose: 4000 });
    } finally {
      setRampBusy(false);
    }
  };

  /** Soft-cap legado: sobe o teto sem promover a trusted (LAC-115 polish). */
  const runGenerousLegacyCap = async () => {
    const cap = tenant?.generous_legacy_cap ?? 10000;
    if (
      !window.confirm(
        t("sa.tenants.ramp.generousCapConfirm", {
          cap: Number(cap).toLocaleString(locale),
        })
      )
    ) {
      return;
    }
    setRampBusy(true);
    setRampError(null);
    try {
      const res = await patchSaTenantSendingRamp(tenantId, {
        generous_legacy_cap: true,
      });
      toast.success(res?.message || t("sa.tenants.ramp.generousCapSuccess"), {
        autoClose: 2500,
      });
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      const msg = e?.message || t("sa.tenants.detail.actions.error");
      setRampError(msg);
      toast.error(msg, { autoClose: 4000 });
    } finally {
      setRampBusy(false);
    }
  };

  const runSaveRamp = async () => {
    const trimmed = rampCapInput.trim();
    let dailySendCap: number | null = null;
    if (trimmed !== "") {
      const parsed = Number(trimmed);
      if (!Number.isInteger(parsed) || parsed < 0) {
        setRampError(t("sa.tenants.ramp.errors.capInvalid"));
        setActionModal("editRamp");
        return;
      }
      dailySendCap = parsed;
    }

    const intervalTrimmed = rampMinIntervalInput.trim();
    const minIntervalParsed = Number(intervalTrimmed === "" ? "0" : intervalTrimmed);
    if (!Number.isInteger(minIntervalParsed) || minIntervalParsed < 0) {
      setRampError(t("sa.tenants.ramp.errors.intervalInvalid"));
      setActionModal("editRamp");
      return;
    }

    setRampBusy(true);
    setRampError(null);
    try {
      const res = await patchSaTenantSendingRamp(tenantId, {
        sending_stage: rampStage,
        daily_send_cap: dailySendCap,
        min_send_interval_seconds: minIntervalParsed,
      });
      toast.success(res?.message || t("sa.tenants.ramp.success"), {
        autoClose: 2500,
      });
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
      setActionModal(null);
    } catch (e: any) {
      // API já manda message leiga — não exibir error.code.
      const msg = e?.message || t("sa.tenants.detail.actions.error");
      setRampError(msg);
      toast.error(msg, { autoClose: 4000 });
      setActionModal("editRamp");
    } finally {
      setRampBusy(false);
    }
  };

  const requestSaveRamp = () => {
    const current = (tenant?.sending_stage || "new") as string;
    // Confirma só ao promover para trusted (libera teto de ramp).
    if (rampStage === "trusted" && current !== "trusted") {
      setActionModal("confirmTrustedRamp");
      return;
    }
    void runSaveRamp();
  };

  // LAC-062: retorna badge de visibilidade do plano.
  const visibilityBadge = (visibility: string | undefined) => {
    if (visibility === "private")
      return (
        <Badge color="warning" className="ms-1">
          {t("sa.plans.visibility.private")}
        </Badge>
      );
    if (visibility === "internal")
      return (
        <Badge color="info" className="ms-1">
          {t("sa.plans.visibility.internal")}
        </Badge>
      );
    return null;
  };

  // LAC-062: confirma atribuição de plano privado.
  const handleEditPlanChange = (newPlanId: number | "") => {
    if (typeof newPlanId !== "number") {
      setEditPlanId(newPlanId);
      return;
    }
    const plan = plans.find((p) => p.id === newPlanId);
    if (plan?.visibility === "private") {
      if (
        !window.confirm(
          t("sa.plans.confirmPrivate", { name: plan.name })
        )
      ) {
        return;
      }
    }
    setEditPlanId(newPlanId);
  };

  const openEditModal = async () => {
    if (!tenant) return;
    setEditName(tenant.name || "");
    setEditDocument(tenant.document || "");
    setEditPhone(tenant.phone_number || "");
    setEditOwnerName(tenant.owner?.name || "");
    setEditOwnerEmail(tenant.owner?.email || "");
    setEditOwnerPassword("");
    setEditPlanId(tenant.subscription?.plan_id || "");
    setEditBillingChannel(
      (tenant.billing_channel as "self_serve" | "brdominio") || "self_serve"
    );
    setEditLocale(tenant.locale || "pt-BR");
    setEditTimezone(tenant.timezone || "America/Sao_Paulo");
    setEditError(null);
    setEditFieldErrors({});
    setActionModal("edit");
    try {
      const res = await getSuperAdminPlans({ is_active: true, per_page: 100 });
      setPlans((res?.data || []).filter((p) => p.is_active));
    } catch {
      /* select vazio — ainda pode salvar outros campos */
    }
  };

  const saveEdit = async () => {
    const nextErrors: Record<string, string> = {};
    if (!editName.trim()) {
      nextErrors.name = t("sa.tenants.edit.errors.name");
    }
    if (!editOwnerName.trim()) {
      nextErrors.owner_name = t("sa.tenants.edit.errors.ownerName");
    }
    if (!editOwnerEmail.trim()) {
      nextErrors.owner_email = t("sa.tenants.edit.errors.ownerEmail");
    }
    if (
      editOwnerPassword.trim() !== "" &&
      editOwnerPassword.trim().length < 8
    ) {
      nextErrors.owner_password = t("sa.tenants.edit.errors.ownerPassword");
    }
    setEditFieldErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setEditBusy(true);
    setEditError(null);
    try {
      const payload: Parameters<typeof patchSaTenant>[1] = {
        name: editName.trim(),
        document: editDocument.trim() || null,
        phone_number: editPhone.trim() || null,
        owner_name: editOwnerName.trim(),
        owner_email: editOwnerEmail.trim().toLowerCase(),
        billing_channel: editBillingChannel,
        locale: editLocale,
        timezone: editTimezone,
      };
      if (typeof editPlanId === "number") {
        payload.plan_id = editPlanId;
      }
      if (editOwnerPassword.trim() !== "") {
        payload.owner_password = editOwnerPassword.trim();
      }
      const res = await patchSaTenant(tenantId, payload);
      toast.success(res?.message || t("sa.tenants.edit.success"), {
        autoClose: 2500,
      });
      if (res?.data) {
        setTenant(res.data);
        setChannelDraft(res.data.billing_channel || editBillingChannel);
      }
      setActionModal(null);
      setEditOwnerPassword("");
    } catch (e: any) {
      const details = e?.details || e?.error?.details;
      if (details && typeof details === "object") {
        const mapped: Record<string, string> = {};
        Object.entries(details).forEach(([key, messages]) => {
          if (Array.isArray(messages) && messages[0]) {
            mapped[key] = String(messages[0]);
          }
        });
        setEditFieldErrors(mapped);
      }
      setEditError(e?.message || t("sa.tenants.detail.actions.error"));
    } finally {
      setEditBusy(false);
    }
  };

  const runSuspend = async () => {
    const reason = blockReason.trim();
    if (!reason) {
      setReasonError(t("sa.tenants.detail.actions.reasonRequired"));
      return;
    }
    setBusy(true);
    setReasonError(null);
    try {
      const res = await patchSaTenant(tenantId, {
        blocked: true,
        block_reason: reason,
      });
      toast.success(res?.message || t("sa.tenants.detail.actions.suspendSuccess"), {
        autoClose: 2500,
      });
      setActionModal(null);
      setBlockReason("");
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.actions.error"), {
        autoClose: 4000,
      });
    } finally {
      setBusy(false);
    }
  };

  const runReactivate = async () => {
    setBusy(true);
    try {
      const res = await patchSaTenant(tenantId, { blocked: false });
      toast.success(
        res?.message || t("sa.tenants.detail.actions.reactivateSuccess"),
        { autoClose: 2500 }
      );
      setActionModal(null);
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.actions.error"), {
        autoClose: 4000,
      });
    } finally {
      setBusy(false);
    }
  };

  const runImpersonate = async () => {
    setBusy(true);
    try {
      const res = await impersonateSaTenant(tenantId);
      if (!res?.success || !res.data?.access_token) {
        toast.error(t("sa.tenants.detail.actions.error"), {
          autoClose: 4000,
        });
        return;
      }
      // Mantém sa_authUser intacto — storage tenant separado (DOC 09 § 4).
      const expiresIn = Number(res.data.expires_in) || 900;
      persistTenantSession({
        access_token: res.data.access_token,
        token_type: res.data.token_type,
        expires_in: expiresIn,
        user: res.data.user,
        tenant: res.data.tenant,
        impersonation: true,
        impersonation_expires_at: new Date(
          Date.now() + expiresIn * 1000
        ).toISOString(),
        impersonation_return_path: `/super-admin/tenants/${tenantId}`,
      });
      toast.success(
        res?.message || t("sa.tenants.detail.actions.success"),
        { autoClose: 1500 }
      );
      window.location.href = "/";
    } catch (e: any) {
      toast.error(
        e?.message || t("sa.tenants.detail.actions.error"),
        { autoClose: 4000 }
      );
      setBusy(false);
    }
  };

  const applyBillingChannel = async (next: string) => {
    setChannelBusy(true);
    try {
      const res = await patchSaTenant(tenantId, { billing_channel: next });
      toast.success(
        res?.message || t("sa.tenants.channel.updated"),
        { autoClose: 2500 }
      );
      if (res?.data) {
        setTenant(res.data);
        setChannelDraft(res.data.billing_channel || next);
      }
      setActionModal(null);
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.actions.error"), {
        autoClose: 3500,
      });
    } finally {
      setChannelBusy(false);
    }
  };

  const onChannelChange = (next: string) => {
    setChannelDraft(next);
    const current = tenant?.billing_channel || "self_serve";
    if (next === current) {
      return;
    }
    // Alerta: sair de Br Domínio exige cartão/checkout no painel depois.
    if (current === "brdominio" && next === "self_serve") {
      setActionModal("channelSelfServe");
      return;
    }
    void applyBillingChannel(next);
  };

  const runResetCircuit = async () => {
    if (!playbookConfirmed) {
      return;
    }
    setBusy(true);
    try {
      const res = await resetSaEmailCircuit(tenantId, {
        playbook_confirmed: true,
        playbook_note: playbookNote.trim() || undefined,
      });
      const resumed =
        typeof res?.data?.resumed_sends === "number"
          ? res.data.resumed_sends
          : null;
      toast.success(
        resumed != null
          ? t("sa.tenants.detail.actions.resetCircuitSuccessCount", {
              count: resumed,
            })
          : res?.message || t("sa.tenants.detail.actions.resetCircuitSuccess"),
        { autoClose: 3000 }
      );
      setActionModal(null);
      setPlaybookChecks([false, false, false, false]);
      setPlaybookNote("");
      if (res?.data) {
        setTenant(res.data);
      } else {
        await load();
      }
    } catch (e: any) {
      toast.error(e?.message || t("sa.tenants.detail.actions.error"), {
        autoClose: 4000,
      });
    } finally {
      setBusy(false);
    }
  };

  const confirmAction = () => {
    if (actionModal === "suspend") {
      void runSuspend();
    } else if (actionModal === "reactivate") {
      void runReactivate();
    } else if (actionModal === "impersonate") {
      void runImpersonate();
    } else if (actionModal === "resetCircuit") {
      void runResetCircuit();
    } else if (actionModal === "channelSelfServe") {
      void applyBillingChannel("self_serve");
    } else if (actionModal === "confirmTrustedRamp") {
      void runSaveRamp();
    } else if (actionModal === "resetOwnerMfa") {
      void runResetOwnerMfa();
    }
  };

  const circuitLabel =
    tenant?.email_circuit_open === null
      ? t("sa.tenants.detail.circuitUnavailable")
      : tenant?.email_circuit_open
        ? t("sa.tenants.detail.circuitOpenFriendly")
        : t("sa.tenants.detail.circuitClosed");

  const stageLabel = (stage: string | undefined) => {
    if (stage === "warming") return t("sa.tenants.ramp.stages.warming");
    if (stage === "trusted") return t("sa.tenants.ramp.stages.trusted");
    return t("sa.tenants.ramp.stages.new");
  };

  const hasCapOverride = tenant?.daily_send_cap != null;
  const effectiveCap = tenant?.sending_ramp?.daily_cap;
  const sentToday = tenant?.sending_ramp?.sent_today ?? 0;
  const remainingToday = tenant?.sending_ramp?.remaining_today;
  const tenantTz = tenant?.timezone || "America/Sao_Paulo";
  const resetAtLocal = formatNextUtcDayResetLocal(tenantTz, locale);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("sa.tenants.detail.title")}
          pageTitle={t("sa.menu.tenants")}
        />
        <ToastContainer closeButton={false} limit={2} />
        <div className="mb-3">
          <Button color="light" size="sm" tag={Link} to="/super-admin/tenants">
            {t("sa.tenants.detail.back")}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        ) : error || !tenant ? (
          <Alert color="danger">
            {error || t("sa.tenants.error.loadOne")}{" "}
            <button
              type="button"
              className="btn btn-link btn-sm p-0 align-baseline"
              onClick={() => load()}
            >
              {t("sa.tenants.retry")}
            </button>
          </Alert>
        ) : (
          <>
            <Card className="mb-3">
              <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <div>
                  <h5 className="card-title mb-1">{tenant.name}</h5>
                  <p className="text-muted mb-0 small">
                    {tenant.owner?.email
                      ? t("sa.tenants.detail.ownerLine", {
                          name: tenant.owner.name,
                          email: tenant.owner.email,
                        })
                      : tenant.from_email || "—"}
                    {tenant.sending_domain
                      ? ` · ${tenant.sending_domain}`
                      : ""}
                  </p>
                  {tenant.owner_invite?.pending && (
                    <div ref={inviteBannerRef}>
                      <Alert
                        color={highlightInvite ? "warning" : "info"}
                        className="mt-2 mb-0 py-2"
                      >
                      <div className="d-flex flex-wrap align-items-start gap-2">
                        <div className="small flex-grow-1">
                          <div>
                            {t("sa.tenants.detail.ownerInvite.pendingHint")}
                          </div>
                          {tenant.owner_invite.expires_at && (
                            <div className="text-muted mt-1">
                              {t("sa.tenants.detail.ownerInvite.expiresAt", {
                                at: new Date(
                                  tenant.owner_invite.expires_at
                                ).toLocaleString(locale),
                              })}
                            </div>
                          )}
                          {tenant.owner_invite.updated_at && (
                            <div className="text-muted">
                              {t("sa.tenants.detail.ownerInvite.lastSentAt", {
                                at: new Date(
                                  tenant.owner_invite.updated_at
                                ).toLocaleString(locale),
                              })}
                            </div>
                          )}
                          {inviteAcceptUrl && (
                            <div className="mt-1 text-break">
                              <code className="small">{inviteAcceptUrl}</code>
                            </div>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-1">
                          <Button
                            color="primary"
                            size="sm"
                            disabled={
                              resendInviteBusy || resendCooldownSec > 0
                            }
                            onClick={() => void onResendOwnerInvite(true)}
                          >
                            {resendInviteBusy ? (
                              <Spinner size="sm" />
                            ) : resendCooldownSec > 0 ? (
                              t("sa.tenants.detail.ownerInvite.resendWait", {
                                seconds: resendCooldownSec,
                              })
                            ) : (
                              t("sa.tenants.detail.ownerInvite.resend")
                            )}
                          </Button>
                          <Button
                            color="secondary"
                            outline
                            size="sm"
                            disabled={resendInviteBusy}
                            onClick={() => void onResendOwnerInvite(false)}
                          >
                            {t("sa.tenants.detail.ownerInvite.generateLink")}
                          </Button>
                          <Button
                            color="primary"
                            outline
                            size="sm"
                            disabled={!inviteAcceptUrl}
                            onClick={() => void copyInviteLink()}
                          >
                            {t("sa.tenants.detail.ownerInvite.copyLink")}
                          </Button>
                        </div>
                      </div>
                      </Alert>
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <Badge
                    color={
                      tenant.billing_channel === "brdominio"
                        ? "info"
                        : "secondary"
                    }
                  >
                    {tenant.billing_channel === "brdominio"
                      ? t("sa.tenants.channel.brdominio")
                      : t("sa.tenants.channel.selfServe")}
                  </Badge>
                  <Badge
                    color={
                      (tenant.preferred_billing_currency || "BRL") === "USD"
                        ? "primary"
                        : "light"
                    }
                    title={t("sa.tenants.detail.preferredMarketHint")}
                  >
                    {t("sa.tenants.detail.preferredMarket", {
                      currency: tenant.preferred_billing_currency || "BRL",
                      market:
                        (tenant.preferred_billing_currency || "BRL") === "USD"
                          ? t("sa.tenants.detail.marketIntl")
                          : t("sa.tenants.detail.marketBr"),
                    })}
                  </Badge>
                  <Badge color={tenant.blocked ? "danger" : "success"}>
                    {tenant.blocked
                      ? t("sa.tenants.status.blocked")
                      : t("sa.tenants.status.active")}
                  </Badge>
                  <Badge
                    color={tenant.sending_setup_ready ? "success" : "warning"}
                  >
                    {tenant.sending_setup_ready
                      ? t("sa.tenants.setup.ready")
                      : t("sa.tenants.setup.pending")}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                <FormGroup className="mb-3" style={{ maxWidth: 360 }}>
                  <Label for="billing-channel-detail">
                    {t("sa.tenants.channel.label")}
                  </Label>
                  <Input
                    id="billing-channel-detail"
                    type="select"
                    bsSize="sm"
                    value={channelDraft}
                    disabled={channelBusy}
                    onChange={(e) => onChannelChange(e.target.value)}
                  >
                    <option value="brdominio">
                      {t("sa.tenants.channel.brdominio")}
                    </option>
                    <option value="self_serve">
                      {t("sa.tenants.channel.selfServe")}
                    </option>
                  </Input>
                  <small className="text-muted">
                    {channelDraft === "brdominio"
                      ? t("sa.tenants.channel.brdominioHint")
                      : t("sa.tenants.channel.selfServeHint")}
                  </small>
                </FormGroup>
                <FormGroup className="mb-3" style={{ maxWidth: 360 }}>
                  <Label for="dns-mode-detail">
                    {t("sa.tenants.dnsMode.label")}
                  </Label>
                  <Input
                    id="dns-mode-detail"
                    type="select"
                    bsSize="sm"
                    value={dnsModeDraft}
                    disabled={dnsModeBusy}
                    onChange={async (e) => {
                      const next = e.target.value === "ns" ? "ns" : "cname";
                      if (!tenant || next === dnsModeDraft) {
                        return;
                      }
                      setDnsModeBusy(true);
                      try {
                        const res = await patchSaTenant(tenantId, {
                          dns_delegation_mode: next,
                        });
                        if (res?.success && res.data) {
                          setTenant(res.data);
                          setDnsModeDraft(
                            res.data.dns_delegation_mode === "ns" ? "ns" : "cname"
                          );
                          toast.success(t("sa.tenants.dnsMode.saved"), {
                            autoClose: 2500,
                          });
                        }
                      } catch (err: any) {
                        toast.error(
                          err?.message || t("sa.tenants.error.loadOne"),
                          { autoClose: 4000 }
                        );
                      } finally {
                        setDnsModeBusy(false);
                      }
                    }}
                  >
                    <option value="cname">
                      {t("sa.tenants.dnsMode.cname")}
                    </option>
                    <option value="ns">{t("sa.tenants.dnsMode.ns")}</option>
                  </Input>
                  <small className="text-muted">
                    {t("sa.tenants.dnsMode.hint")}{" "}
                    <Link to="/super-admin/dns/ns-guide">
                      {t("sa.menu.dnsNsGuide")}
                    </Link>
                  </small>
                </FormGroup>
                {tenant.tracking_ssl_attention ? (
                  <Alert color="warning" className="mb-3 py-2">
                    <div className="fw-medium">
                      {t("sa.tenants.sslAttention.title")}
                    </div>
                    <div className="small mb-0 mt-1">
                      {t("sa.tenants.sslAttention.body")}
                    </div>
                  </Alert>
                ) : null}
                {tenant.blocked && tenant.block_reason ? (
                  <Alert color="warning" className="mb-3 py-2">
                    {t("sa.tenants.detail.blockReason", {
                      reason: tenant.block_reason,
                    })}
                  </Alert>
                ) : null}
                {tenant.email_circuit_open === true ? (
                  <Alert color="danger" className="mb-3 py-2">
                    {t("sa.tenants.detail.circuitOpenFriendly")}
                    {tenant.email_circuit_open_at
                      ? ` · ${new Date(
                          tenant.email_circuit_open_at
                        ).toLocaleString(locale)}`
                      : ""}
                  </Alert>
                ) : null}
                {(tenant.sends_awaiting_sa_resume ?? 0) > 0 ? (
                  <Alert color="danger" className="mb-3 py-2">
                    {t("sa.tenants.detail.awaitingSaBanner", {
                      count: tenant.sends_awaiting_sa_resume,
                    })}{" "}
                    <Link
                      to={`/super-admin/sending?needs_sa_resume=1&tenant_id=${tenant.id}`}
                      className="alert-link"
                    >
                      {t("sa.tenants.detail.awaitingSaCta")}
                    </Link>
                  </Alert>
                ) : null}
                {tenant.email_circuit_open === true ||
                (tenant.sends_awaiting_sa_resume ?? 0) > 0 ? (
                  <Alert color="warning" className="mb-3 py-2">
                    <p className="fw-medium mb-2">
                      {t("sa.tenants.detail.playbookTitle")}
                    </p>
                    <ol className="mb-0 ps-3 small">
                      <li>{t("sa.tenants.detail.playbook.1")}</li>
                      <li>{t("sa.tenants.detail.playbook.2")}</li>
                      <li>{t("sa.tenants.detail.playbook.3")}</li>
                      <li>{t("sa.tenants.detail.playbook.4")}</li>
                    </ol>
                  </Alert>
                ) : null}
                {(tenant.awaiting_sa_sends?.length ?? 0) > 0 ? (
                  <Card className="mb-3 border border-danger-subtle">
                    <CardHeader className="py-2">
                      <h6 className="mb-0">
                        {t("sa.tenants.detail.awaitingSends.title")}
                      </h6>
                    </CardHeader>
                    <CardBody className="py-2">
                      <ul className="list-unstyled mb-0">
                        {tenant.awaiting_sa_sends!.map((send) => (
                          <li
                            key={send.id}
                            className="d-flex flex-wrap align-items-center justify-content-between gap-2 py-2 border-bottom border-light"
                          >
                            <div className="small">
                              <span className="fw-medium">#{send.id}</span>
                              {send.campaign_name
                                ? ` · ${send.campaign_name}`
                                : ""}
                              {send.subject ? (
                                <span className="text-muted d-block">
                                  {send.subject}
                                </span>
                              ) : null}
                              <span className="text-muted">
                                {t("sa.tenants.detail.awaitingSends.pauses", {
                                  count: send.circuit_pause_count ?? 0,
                                })}
                              </span>
                            </div>
                            <Button
                              color="danger"
                              outline
                              size="sm"
                              disabled={resumingSendId === send.id}
                              onClick={() => void runResumeSend(send.id)}
                            >
                              {resumingSendId === send.id ? (
                                <Spinner size="sm" />
                              ) : (
                                t("sa.tenants.detail.awaitingSends.resume")
                              )}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </CardBody>
                  </Card>
                ) : null}
                <Row className="g-3 text-muted small">
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.detail.ses")}
                    </strong>
                    {tenant.ses_identity_status || "—"}
                    {tenant.ses_identity_status === "failed" ? (
                      <div className="mt-2">
                        <Button
                          color="warning"
                          size="sm"
                          className="me-2"
                          disabled={recreatingSes}
                          onClick={() => void runRecreateSes()}
                        >
                          {recreatingSes ? (
                            <Spinner size="sm" />
                          ) : (
                            t("sa.tenants.detail.sesRecreate.button")
                          )}
                        </Button>
                        <FormGroup check inline className="mb-0">
                          <Input
                            id="ses-recreate-force"
                            type="checkbox"
                            checked={recreateSesForce}
                            onChange={(e) =>
                              setRecreateSesForce(e.target.checked)
                            }
                          />
                          <Label check for="ses-recreate-force" className="small">
                            {t("sa.tenants.detail.sesRecreate.force")}
                          </Label>
                        </FormGroup>
                        <p className="text-muted small mb-0 mt-1">
                          {t("sa.tenants.detail.sesRecreate.hint")}
                        </p>
                      </div>
                    ) : null}
                  </Col>
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.detail.tracking")}
                    </strong>
                    {tenant.tracking_status || "—"}
                  </Col>
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.detail.circuit")}
                    </strong>
                    {circuitLabel}
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <SesByoCard
              tenant={tenant}
              onUpdated={(next) => setTenant(next)}
            />

            {(tenant.circuit_history?.length ?? 0) > 0 ||
            (tenant.stats_series_7d?.length ?? 0) > 0 ? (
              <Row className="g-3 mb-3">
                {(tenant.circuit_history?.length ?? 0) > 0 ? (
                  <Col lg={6}>
                    <Card className="h-100 mb-0">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("sa.tenants.detail.circuitHistoryTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody className="py-2">
                        <ul className="list-unstyled mb-0 small">
                          {(tenant.circuit_history || []).map((row, idx) => (
                            <li
                              key={`${row.action}-${row.at}-${idx}`}
                              className="d-flex flex-wrap justify-content-between gap-2 py-2 border-bottom border-light"
                            >
                              <span>
                                {t(`sa.activityLogs.actions.${row.action}`, {
                                  defaultValue: row.action,
                                })}
                                {row.actor ? (
                                  <span className="text-muted">
                                    {" "}
                                    · {row.actor}
                                  </span>
                                ) : null}
                              </span>
                              <span className="text-muted">
                                {row.at
                                  ? new Date(row.at).toLocaleString(locale)
                                  : "—"}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </CardBody>
                    </Card>
                  </Col>
                ) : null}
                {(tenant.stats_series_7d?.length ?? 0) > 0 ? (
                  <Col lg={tenant.circuit_history?.length ? 6 : 12}>
                    <div ref={sparkCardRef}>
                      <Card
                        className={`h-100 mb-0${sparkHighlightDate ? " border-primary" : ""}`}
                      >
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("sa.tenants.detail.sparkTitle")}
                        </h5>
                        <p className="text-muted small mb-0 mt-1">
                          {sparkHighlightDate
                            ? t("sa.tenants.detail.sparkHighlightHint", {
                                date: (() => {
                                  try {
                                    return new Intl.DateTimeFormat(locale, {
                                      day: "2-digit",
                                      month: "short",
                                      year: "numeric",
                                      timeZone: "UTC",
                                    }).format(
                                      new Date(`${sparkHighlightDate}T12:00:00Z`)
                                    );
                                  } catch {
                                    return sparkHighlightDate;
                                  }
                                })(),
                              })
                            : t("sa.tenants.detail.sparkClickHint")}
                        </p>
                      </CardHeader>
                      <CardBody>
                        <SafeApexChart
                          dir="ltr"
                          type="line"
                          height={180}
                          className="apex-charts"
                          options={{
                            chart: {
                              type: "line",
                              toolbar: { show: false },
                              zoom: { enabled: false },
                              events: {
                                // LAC-142: clique no dia → Envios do cliente filtrados por data UTC.
                                markerClick: (
                                  _e: unknown,
                                  _ctx: unknown,
                                  config: { dataPointIndex?: number }
                                ) => {
                                  const idx = config?.dataPointIndex;
                                  const point =
                                    idx != null && idx >= 0
                                      ? tenant?.stats_series_7d?.[idx]
                                      : undefined;
                                  if (!point?.date || !tenant?.id) {
                                    return;
                                  }
                                  navigate(
                                    `/super-admin/sending?tenant_id=${tenant.id}&date=${encodeURIComponent(point.date)}`
                                  );
                                },
                              },
                            },
                            stroke: { width: 2, curve: "smooth" },
                            dataLabels: { enabled: false },
                            colors: ["#f06548", "#f7b84b"],
                            legend: { position: "top" },
                            markers: {
                              size: 4,
                              hover: { size: 6 },
                            },
                            annotations: (() => {
                              const series = tenant.stats_series_7d || [];
                              const idx = series.findIndex(
                                (p) => p.date === sparkHighlightDate
                              );
                              if (idx < 0) {
                                return undefined;
                              }
                              let label: string;
                              try {
                                label = new Intl.DateTimeFormat(locale, {
                                  day: "2-digit",
                                  month: "short",
                                }).format(
                                  new Date(`${series[idx].date}T12:00:00Z`)
                                );
                              } catch {
                                label = series[idx].date;
                              }
                              return {
                                xaxis: [
                                  {
                                    x: label,
                                    borderColor: "#405189",
                                    strokeDashArray: 0,
                                    label: {
                                      text: t(
                                        "sa.tenants.detail.sparkHighlightLabel"
                                      ),
                                      style: {
                                        color: "#fff",
                                        background: "#405189",
                                      },
                                    },
                                  },
                                ],
                              };
                            })(),
                            xaxis: {
                              categories: (tenant.stats_series_7d || []).map(
                                (p) => {
                                  try {
                                    return new Intl.DateTimeFormat(locale, {
                                      day: "2-digit",
                                      month: "short",
                                    }).format(
                                      new Date(`${p.date}T12:00:00Z`)
                                    );
                                  } catch {
                                    return p.date;
                                  }
                                }
                              ),
                            },
                            yaxis: { min: 0, decimalsInFloat: 2 },
                          } as ApexOptions}
                          series={[
                            {
                              name: t("sa.tenants.detail.sparkBounce"),
                              data: (tenant.stats_series_7d || []).map(
                                (p) => p.bounce_rate
                              ),
                            },
                            {
                              name: t("sa.tenants.detail.sparkComplaint"),
                              data: (tenant.stats_series_7d || []).map(
                                (p) => p.complaint_rate
                              ),
                            },
                          ]}
                        />
                      </CardBody>
                    </Card>
                    </div>
                  </Col>
                ) : null}
              </Row>
            ) : null}

            <Row className="g-3 mb-3">
              <Col lg={6}>
                <Card className="h-100 mb-0">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("sa.tenants.detail.quotaTitle")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    {!tenant.subscription ? (
                      <p className="text-muted mb-0">
                        {t("sa.tenants.detail.noSubscription")}
                      </p>
                    ) : (
                      <>
                        <p className="mb-1">
                          {t("sa.tenants.detail.plan", {
                            name: tenant.subscription.plan_name || "—",
                          })}
                        </p>
                        <p className="mb-1">
                          {t("sa.tenants.detail.quotaUsage", {
                            used: tenant.subscription.emails_used_in_period.toLocaleString(
                              locale
                            ),
                            quota: tenant.subscription.email_quota.toLocaleString(
                              locale
                            ),
                          })}
                        </p>
                        <p className="text-muted small mb-0">
                          {t("sa.tenants.detail.subStatus", {
                            status: tenant.subscription.status,
                          })}
                        </p>
                      </>
                    )}
                    {/* LAC-100: último webhook MP — só metadados (nunca secret/payload). */}
                    <hr className="my-3" />
                    <h6 className="text-muted text-uppercase small mb-2">
                      {t("sa.tenants.detail.mpWebhook.title")}
                    </h6>
                    {!tenant.last_mercadopago_webhook ? (
                      <p className="text-muted small mb-0">
                        {t("sa.tenants.detail.mpWebhook.empty")}
                      </p>
                    ) : (
                      <div className="small">
                        <p className="mb-1">
                          <strong>
                            {t("sa.tenants.detail.mpWebhook.eventId")}:
                          </strong>{" "}
                          {tenant.last_mercadopago_webhook.external_event_id ||
                            "—"}
                        </p>
                        <p className="mb-1">
                          <strong>
                            {t("sa.tenants.detail.mpWebhook.status")}:
                          </strong>{" "}
                          {tenant.last_mercadopago_webhook.http_status ?? "—"}
                        </p>
                        <p className="mb-0 text-muted">
                          {t("sa.tenants.detail.mpWebhook.at", {
                            at: new Date(
                              tenant.last_mercadopago_webhook.processed_at ||
                                tenant.last_mercadopago_webhook.created_at ||
                                ""
                            ).toLocaleString(locale),
                          })}
                        </p>
                        {tenant.last_mercadopago_webhook.error_message ? (
                          <p className="text-danger small mb-0 mt-1">
                            {tenant.last_mercadopago_webhook.error_message}
                          </p>
                        ) : null}
                      </div>
                    )}
                    {/* Preview ops: quais e-mails de cobrança já saíram (sem HTML). */}
                    <hr className="my-3" />
                    <h6 className="text-muted text-uppercase small mb-2">
                      {t("sa.tenants.detail.dunningMails.title")}
                    </h6>
                    {!tenant.dunning_mails ||
                    tenant.dunning_mails.length === 0 ? (
                      <p className="text-muted small mb-0">
                        {t("sa.tenants.detail.dunningMails.empty")}
                      </p>
                    ) : (
                      <ul className="list-unstyled small mb-0">
                        {tenant.dunning_mails.map((mail, idx) => (
                          <li key={`${mail.action}-${idx}`} className="mb-1">
                            <strong>
                              {t(
                                `sa.tenants.detail.dunningMails.kind.${mail.kind}`,
                                { defaultValue: mail.kind }
                              )}
                            </strong>
                            {mail.at ? (
                              <span className="text-muted">
                                {" "}
                                —{" "}
                                {new Date(mail.at).toLocaleString(locale)}
                              </span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    )}
                  </CardBody>
                </Card>
              </Col>
              <Col lg={6}>
                <Card className="h-100 mb-0">
                  <CardHeader>
                    <h5 className="card-title mb-0">
                      {t("sa.tenants.detail.statsTitle")}
                    </h5>
                  </CardHeader>
                  <CardBody>
                    <Row className="g-2">
                      <Col xs={6}>
                        <p className="text-muted small mb-0">
                          {t("sa.tenants.detail.sent")}
                        </p>
                        <h5>
                          {tenant.stats_7d.sent.toLocaleString(locale)}
                        </h5>
                      </Col>
                      <Col xs={6}>
                        <p className="text-muted small mb-0">
                          {t("sa.tenants.detail.bounceRate")}
                        </p>
                        <h5>
                          {formatSaRate(tenant.stats_7d.bounce_rate, locale)}
                        </h5>
                      </Col>
                      <Col xs={6}>
                        <p className="text-muted small mb-0">
                          {t("sa.tenants.detail.complaintRate")}
                        </p>
                        <h5>
                          {formatSaRate(
                            tenant.stats_7d.complaint_rate,
                            locale
                          )}
                        </h5>
                      </Col>
                      <Col xs={6}>
                        <p className="text-muted small mb-0">
                          {t("sa.tenants.detail.failed")}
                        </p>
                        <h5>
                          {tenant.stats_7d.failed.toLocaleString(locale)}
                        </h5>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <div ref={rampCardRef} id="sa-tenant-ramp">
            <Card
              className={`mb-3${highlightRamp ? " border-warning" : ""}`}
            >
              <CardHeader className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                <h5 className="card-title mb-0">
                  {t("sa.tenants.ramp.title")}
                </h5>
                <div className="d-flex gap-2 flex-wrap align-items-center">
                  {hasCapOverride ? (
                    <Badge color="warning" pill>
                      {t("sa.tenants.ramp.overrideActive")}
                    </Badge>
                  ) : null}
                  {tenant.sending_stage !== "trusted" ? (
                    <Button
                      color="success"
                      outline
                      size="sm"
                      disabled={rampBusy}
                      onClick={() => void runLiberarLimiteDiario()}
                      title={t("sa.tenants.ramp.liberarHint")}
                    >
                      {t("sa.tenants.ramp.liberarCta")}
                    </Button>
                  ) : null}
                  {tenant.sending_stage !== "trusted" ? (
                    <Button
                      color="info"
                      outline
                      size="sm"
                      disabled={rampBusy}
                      onClick={() => void runGenerousLegacyCap()}
                      title={t("sa.tenants.ramp.generousCapHint")}
                    >
                      {t("sa.tenants.ramp.generousCapCta")}
                    </Button>
                  ) : null}
                  <Button
                    color="primary"
                    outline
                    size="sm"
                    onClick={openRampModal}
                  >
                    {t("sa.tenants.ramp.edit")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-muted small mb-2">
                  {t("sa.tenants.ramp.hint")}
                </p>
                {effectiveCap != null ? (
                  <p className="text-muted small mb-3">
                    {t("sa.tenants.ramp.resetHint", {
                      resetAt: resetAtLocal,
                      timezone: tenantTimezoneDisplayLabel(tenantTz, t),
                    })}
                  </p>
                ) : null}
                <Row className="g-3 mb-3">
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.ramp.stageLabel")}
                    </strong>
                    <span>{stageLabel(tenant.sending_stage)}</span>
                  </Col>
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.ramp.capOverrideLabel")}
                    </strong>
                    <span>
                      {hasCapOverride
                        ? Number(tenant.daily_send_cap).toLocaleString(locale)
                        : t("sa.tenants.ramp.capDefault")}
                    </span>
                  </Col>
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.ramp.todayLabel")}
                    </strong>
                    <span>
                      {effectiveCap == null
                        ? t("sa.tenants.ramp.todayUnlimited", {
                            sent: sentToday.toLocaleString(locale),
                          })
                        : t("sa.tenants.ramp.todayUsage", {
                            sent: sentToday.toLocaleString(locale),
                            cap: Number(effectiveCap).toLocaleString(locale),
                            remaining: Number(
                              remainingToday ?? 0
                            ).toLocaleString(locale),
                          })}
                    </span>
                  </Col>
                  <Col md={4}>
                    <strong className="d-block text-body">
                      {t("sa.tenants.ramp.minIntervalLabel")}
                    </strong>
                    <span>
                      {(tenant.min_send_interval_seconds ?? 0) > 0
                        ? t("sa.tenants.ramp.minIntervalValue", {
                            seconds: tenant.min_send_interval_seconds,
                          })
                        : t("sa.tenants.ramp.minIntervalNone")}
                    </span>
                  </Col>
                </Row>
                <div className="d-flex flex-wrap gap-2 mb-3">
                  <Button
                    color="light"
                    size="sm"
                    tag={Link}
                    to={`/super-admin/sending?preset=canary&tenant_id=${tenant.id}`}
                  >
                    {t("sa.tenants.ramp.linkCanary")}
                  </Button>
                  <Button
                    color="light"
                    size="sm"
                    tag={Link}
                    to={`/super-admin/activity-logs?action=tenant.ramp_updated&tenant_id=${tenant.id}`}
                  >
                    {t("sa.tenants.ramp.linkHistory")}
                  </Button>
                </div>
                {rampLogs.length > 0 ? (
                  <div className="border-top pt-3">
                    <p className="fw-medium small mb-2">
                      {t("sa.tenants.ramp.recentChanges")}
                    </p>
                    <ul className="list-unstyled mb-0 small text-muted">
                      {rampLogs.map((log) => {
                        const stage =
                          (log.new_values?.sending_stage as string) || "—";
                        const when = log.created_at
                          ? new Date(log.created_at).toLocaleString(locale)
                          : "—";
                        return (
                          <li key={log.id} className="mb-1">
                            {t("sa.tenants.ramp.recentChangeLine", {
                              when,
                              stage: stageLabel(stage),
                            })}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ) : null}
              </CardBody>
            </Card>
            </div>

            <Card className="mb-0">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("sa.tenants.detail.actionsTitle")}
                </h5>
              </CardHeader>
              <CardBody className="d-flex flex-wrap gap-2">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => void openEditModal()}
                >
                  {t("sa.tenants.edit.open")}
                </Button>
                {tenant.owner?.mfa_enabled ? (
                  <Button
                    color="warning"
                    outline
                    size="sm"
                    disabled={mfaResetCooldownSec > 0}
                    onClick={() => {
                      setMfaResetMode("recovery");
                      setMfaRecoveryCodes(null);
                      setMfaCodesSentConfirmed(false);
                      setActionModal("resetOwnerMfa");
                    }}
                  >
                    {mfaResetCooldownSec > 0
                      ? t("sa.tenants.detail.ownerMfa.wait", {
                          seconds: mfaResetCooldownSec,
                        })
                      : t("sa.tenants.detail.ownerMfa.open")}
                  </Button>
                ) : null}
                {!tenant.blocked ? (
                  <Button
                    color="danger"
                    size="sm"
                    onClick={() => {
                      setBlockReason("");
                      setReasonError(null);
                      setActionModal("suspend");
                    }}
                  >
                    {t("sa.tenants.detail.actions.suspend")}
                  </Button>
                ) : (
                  <Button
                    color="success"
                    size="sm"
                    onClick={() => setActionModal("reactivate")}
                  >
                    {t("sa.tenants.detail.actions.reactivate")}
                  </Button>
                )}
                <Button
                  color="primary"
                  outline
                  size="sm"
                  disabled={tenant.blocked}
                  onClick={() => setActionModal("impersonate")}
                >
                  {t("sa.tenants.detail.actions.impersonate")}
                </Button>
                {tenant.email_circuit_open === true ||
                (tenant.sends_awaiting_sa_resume ?? 0) > 0 ? (
                  <Button
                    color="warning"
                    size="sm"
                    onClick={openResetCircuitModal}
                  >
                    {t("sa.tenants.detail.actions.resetCircuit")}
                  </Button>
                ) : null}
              </CardBody>
            </Card>
          </>
        )}

        <Modal
          isOpen={actionModal !== null}
          toggle={closeModal}
          centered
          size={actionModal === "edit" ? "lg" : undefined}
        >
          <ModalHeader toggle={closeModal}>
            {actionModal === "edit" && t("sa.tenants.edit.title")}
            {actionModal === "editRamp" && t("sa.tenants.ramp.editTitle")}
            {actionModal === "confirmTrustedRamp" &&
              t("sa.tenants.ramp.trustedConfirmTitle")}
            {actionModal === "suspend" &&
              t("sa.tenants.detail.actions.suspend")}
            {actionModal === "reactivate" &&
              t("sa.tenants.detail.actions.reactivate")}
            {actionModal === "impersonate" &&
              t("sa.tenants.detail.actions.impersonate")}
            {actionModal === "resetCircuit" &&
              t("sa.tenants.detail.actions.resetCircuit")}
            {actionModal === "channelSelfServe" &&
              t("sa.tenants.channel.switchTitle")}
            {actionModal === "resetOwnerMfa" &&
              t("sa.tenants.detail.ownerMfa.title")}
          </ModalHeader>
          <ModalBody>
            {actionModal === "editRamp" ? (
              <>
                <Alert color="info" className="py-2">
                  {t("sa.tenants.ramp.editHint")}
                </Alert>
                {rampError ? (
                  <Alert color="danger" className="py-2">
                    {rampError}
                  </Alert>
                ) : null}
                <FormGroup>
                  <Label for="ramp-stage">
                    {t("sa.tenants.ramp.stageLabel")}
                  </Label>
                  <Input
                    id="ramp-stage"
                    type="select"
                    value={rampStage}
                    onChange={(e) =>
                      setRampStage(e.target.value as SendingStageValue)
                    }
                  >
                    <option value="new">
                      {t("sa.tenants.ramp.stages.new")}
                    </option>
                    <option value="warming">
                      {t("sa.tenants.ramp.stages.warming")}
                    </option>
                    <option value="trusted">
                      {t("sa.tenants.ramp.stages.trusted")}
                    </option>
                  </Input>
                </FormGroup>
                <FormGroup className="mb-3">
                  <Label for="ramp-cap">
                    {t("sa.tenants.ramp.capOverrideLabel")}
                  </Label>
                  <Input
                    id="ramp-cap"
                    type="number"
                    min={0}
                    step={1}
                    value={rampCapInput}
                    onChange={(e) => setRampCapInput(e.target.value)}
                    placeholder={t("sa.tenants.ramp.capPlaceholder")}
                  />
                  <small className="text-muted">
                    {t("sa.tenants.ramp.capHint")}
                  </small>
                </FormGroup>
                <FormGroup className="mb-0">
                  <Label for="ramp-min-interval">
                    {t("sa.tenants.ramp.minIntervalLabel")}
                  </Label>
                  <Input
                    id="ramp-min-interval"
                    type="number"
                    min={0}
                    step={1}
                    value={rampMinIntervalInput}
                    onChange={(e) => setRampMinIntervalInput(e.target.value)}
                    placeholder={t("sa.tenants.ramp.minIntervalPlaceholder")}
                  />
                  <small className="text-muted">
                    {t("sa.tenants.ramp.minIntervalHint")}
                  </small>
                </FormGroup>
              </>
            ) : null}
            {actionModal === "confirmTrustedRamp" ? (
              <p className="mb-0">{t("sa.tenants.ramp.trustedConfirm")}</p>
            ) : null}
            {actionModal === "edit" ? (
              <>
                <Alert color="info" className="py-2">
                  {t("sa.tenants.edit.hint")}
                </Alert>
                {editError ? (
                  <Alert color="danger" className="py-2">
                    {editError}
                  </Alert>
                ) : null}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-name">
                        {t("sa.tenants.new.fields.name")}
                      </Label>
                      <Input
                        id="edit-name"
                        value={editName}
                        invalid={!!editFieldErrors.name}
                        onChange={(e) => setEditName(e.target.value)}
                      />
                      <FormFeedback>{editFieldErrors.name}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-channel">
                        {t("sa.tenants.channel.label")}
                      </Label>
                      <Input
                        id="edit-channel"
                        type="select"
                        value={editBillingChannel}
                        onChange={(e) =>
                          setEditBillingChannel(
                            e.target.value as "self_serve" | "brdominio"
                          )
                        }
                      >
                        <option value="brdominio">
                          {t("sa.tenants.channel.brdominio")}
                        </option>
                        <option value="self_serve">
                          {t("sa.tenants.channel.selfServe")}
                        </option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-owner-name">
                        {t("sa.tenants.new.fields.ownerName")}
                      </Label>
                      <Input
                        id="edit-owner-name"
                        value={editOwnerName}
                        invalid={!!editFieldErrors.owner_name}
                        onChange={(e) => setEditOwnerName(e.target.value)}
                      />
                      <FormFeedback>{editFieldErrors.owner_name}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-owner-email">
                        {t("sa.tenants.new.fields.ownerEmail")}
                      </Label>
                      <Input
                        id="edit-owner-email"
                        type="email"
                        value={editOwnerEmail}
                        invalid={!!editFieldErrors.owner_email}
                        onChange={(e) => setEditOwnerEmail(e.target.value)}
                      />
                      <FormFeedback>{editFieldErrors.owner_email}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="edit-owner-password">
                    {t("sa.tenants.edit.passwordOptional")}
                  </Label>
                  <div className="d-flex gap-2">
                    <Input
                      id="edit-owner-password"
                      type={editShowPassword ? "text" : "password"}
                      value={editOwnerPassword}
                      invalid={!!editFieldErrors.owner_password}
                      onChange={(e) => setEditOwnerPassword(e.target.value)}
                      placeholder={t("sa.tenants.edit.passwordPlaceholder")}
                      autoComplete="new-password"
                    />
                    <Button
                      color="light"
                      type="button"
                      onClick={() => setEditShowPassword((v) => !v)}
                    >
                      <i
                        className={
                          editShowPassword ? "ri-eye-off-line" : "ri-eye-line"
                        }
                      />
                    </Button>
                  </div>
                  <FormFeedback className="d-block">
                    {editFieldErrors.owner_password}
                  </FormFeedback>
                  <small className="text-muted">
                    {t("sa.tenants.edit.passwordHint")}
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="edit-plan">{t("sa.tenants.new.fields.plan")}</Label>
                  <Input
                    id="edit-plan"
                    type="select"
                    value={editPlanId}
                    onChange={(e) =>
                      handleEditPlanChange(
                        e.target.value ? Number(e.target.value) : ""
                      )
                    }
                  >
                    <option value="">
                      {t("sa.tenants.new.planPlaceholder")}
                    </option>
                    {plans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                        {p.visibility === "private" ? ` [${t("sa.plans.visibility.private")}]` : ""}
                        {p.visibility === "internal" ? ` [${t("sa.plans.visibility.internal")}]` : ""}
                        {" — "}
                        {t("sa.tenants.new.quotaPreview", {
                          quota: p.email_quota,
                        })}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-document">
                        {t("sa.tenants.new.fields.document")}
                      </Label>
                      <Input
                        id="edit-document"
                        value={editDocument}
                        onChange={(e) => setEditDocument(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-phone">
                        {t("sa.tenants.new.fields.phone")}
                      </Label>
                      <Input
                        id="edit-phone"
                        value={editPhone}
                        onChange={(e) => setEditPhone(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-locale">
                        {t("sa.tenants.edit.locale")}
                      </Label>
                      <Input
                        id="edit-locale"
                        type="select"
                        value={editLocale}
                        onChange={(e) => setEditLocale(e.target.value)}
                      >
                        <option value="pt-BR">pt-BR</option>
                        <option value="en">en</option>
                        <option value="es">es</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="edit-timezone">
                        {t("sa.tenants.edit.timezone")}
                      </Label>
                      <Input
                        id="edit-timezone"
                        value={editTimezone}
                        onChange={(e) => setEditTimezone(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </>
            ) : null}
            {actionModal === "suspend" ? (
              <>
                <p className="mb-3">
                  {t("sa.tenants.detail.actions.suspendConfirm")}
                </p>
                <Label for="sa-block-reason">
                  {t("sa.tenants.detail.actions.reasonPlaceholder")}
                </Label>
                <Input
                  id="sa-block-reason"
                  type="textarea"
                  rows={3}
                  value={blockReason}
                  invalid={!!reasonError}
                  onChange={(e) => {
                    setBlockReason(e.target.value);
                    setReasonError(null);
                  }}
                  placeholder={t(
                    "sa.tenants.detail.actions.reasonPlaceholder"
                  )}
                />
                {reasonError ? (
                  <FormFeedback className="d-block">{reasonError}</FormFeedback>
                ) : null}
              </>
            ) : null}
            {actionModal === "reactivate" ? (
              <p className="mb-0">
                {t("sa.tenants.detail.actions.reactivateConfirm")}
              </p>
            ) : null}
            {actionModal === "impersonate" ? (
              <p className="mb-0">
                {t("sa.tenants.detail.actions.impersonateConfirm")}
              </p>
            ) : null}
            {actionModal === "resetCircuit" ? (
              <>
                <p className="mb-3">
                  {t("sa.tenants.detail.actions.resetCircuitConfirm")}
                </p>
                <p className="small text-muted mb-2">
                  {t("sa.tenants.detail.playbook.modalHint")}
                </p>
                {[1, 2, 3, 4].map((n) => (
                  <FormGroup check key={n} className="mb-2">
                    <Input
                      id={`playbook-check-${n}`}
                      type="checkbox"
                      checked={playbookChecks[n - 1]}
                      onChange={(e) => {
                        const next = [...playbookChecks];
                        next[n - 1] = e.target.checked;
                        setPlaybookChecks(next);
                      }}
                    />
                    <Label check for={`playbook-check-${n}`} className="small">
                      {t(`sa.tenants.detail.playbook.${n}`)}
                    </Label>
                  </FormGroup>
                ))}
                <FormGroup className="mb-0 mt-2">
                  <Label for="playbook-note" className="small">
                    {t("sa.tenants.detail.playbook.noteLabel")}
                  </Label>
                  <Input
                    id="playbook-note"
                    type="textarea"
                    rows={2}
                    value={playbookNote}
                    onChange={(e) => setPlaybookNote(e.target.value)}
                    placeholder={t("sa.tenants.detail.playbook.notePlaceholder")}
                  />
                </FormGroup>
              </>
            ) : null}
            {actionModal === "channelSelfServe" ? (
              <p className="mb-0">{t("sa.tenants.channel.switchConfirm")}</p>
            ) : null}
            {actionModal === "resetOwnerMfa" ? (
              mfaRecoveryCodes?.length ? (
                <>
                  <Alert color="success" className="py-2">
                    {t("sa.tenants.detail.ownerMfa.recoveryHint")}
                  </Alert>
                  <pre
                    className="bg-light border rounded p-2 small mb-2"
                    style={{ maxHeight: 200, overflow: "auto" }}
                  >
                    {mfaRecoveryCodes.join("\n")}
                  </pre>
                  <Button
                    color="primary"
                    size="sm"
                    outline
                    type="button"
                    onClick={() => void copyMfaRecoveryCodes()}
                  >
                    {t("sa.tenants.detail.ownerMfa.copyCodes")}
                  </Button>
                  <FormGroup check className="mt-3 mb-0">
                    <Input
                      id="mfa-codes-sent"
                      type="checkbox"
                      checked={mfaCodesSentConfirmed}
                      onChange={(e) =>
                        setMfaCodesSentConfirmed(e.target.checked)
                      }
                    />
                    <Label check for="mfa-codes-sent" className="small">
                      {t("sa.tenants.detail.ownerMfa.codesSentCheck")}
                    </Label>
                  </FormGroup>
                </>
              ) : (
                <>
                  <Alert color="info" className="py-2">
                    {t("sa.tenants.detail.ownerMfa.confirmRecovery", {
                      email: tenant?.owner?.email || "",
                    })}
                  </Alert>
                  <FormGroup check className="mb-2">
                    <Input
                      id="mfa-mode-recovery"
                      type="radio"
                      name="mfa-reset-mode"
                      checked={mfaResetMode === "recovery"}
                      onChange={() => setMfaResetMode("recovery")}
                    />
                    <Label check for="mfa-mode-recovery">
                      {t("sa.tenants.detail.ownerMfa.modeRecovery")}
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mb-2">
                    <Input
                      id="mfa-mode-disable"
                      type="radio"
                      name="mfa-reset-mode"
                      checked={mfaResetMode === "disable"}
                      onChange={() => setMfaResetMode("disable")}
                    />
                    <Label check for="mfa-mode-disable">
                      {t("sa.tenants.detail.ownerMfa.modeDisable")}
                    </Label>
                  </FormGroup>
                  {mfaResetMode === "disable" ? (
                    <Alert color="warning" className="mb-0 py-2">
                      {t("sa.tenants.detail.ownerMfa.confirm", {
                        email: tenant?.owner?.email || "",
                      })}
                    </Alert>
                  ) : null}
                </>
              )
            ) : null}
          </ModalBody>
          <ModalFooter>
            <Button
              color="light"
              disabled={busy || channelBusy || editBusy || rampBusy}
              onClick={() => {
                if (actionModal === "confirmTrustedRamp") {
                  setActionModal("editRamp");
                  return;
                }
                closeModal();
              }}
            >
              {actionModal === "confirmTrustedRamp"
                ? t("sa.tenants.ramp.backToEdit")
                : t("sa.tenants.detail.actions.cancel")}
            </Button>
            {actionModal === "edit" ? (
              <Button
                color="primary"
                disabled={editBusy}
                onClick={() => void saveEdit()}
              >
                {editBusy ? (
                  <Spinner size="sm" />
                ) : (
                  t("sa.tenants.edit.save")
                )}
              </Button>
            ) : actionModal === "editRamp" ? (
              <Button
                color="primary"
                disabled={rampBusy}
                onClick={requestSaveRamp}
              >
                {rampBusy ? (
                  <Spinner size="sm" />
                ) : (
                  t("sa.tenants.ramp.save")
                )}
              </Button>
            ) : actionModal === "resetOwnerMfa" && mfaRecoveryCodes?.length ? (
              <Button
                color="primary"
                disabled={!mfaCodesSentConfirmed}
                onClick={closeModal}
              >
                {t("sa.tenants.detail.ownerMfa.done")}
              </Button>
            ) : (
              <Button
                color={
                  actionModal === "suspend" ||
                  actionModal === "resetCircuit" ||
                  actionModal === "channelSelfServe" ||
                  actionModal === "confirmTrustedRamp" ||
                  (actionModal === "resetOwnerMfa" && mfaResetMode === "disable")
                    ? "danger"
                    : "primary"
                }
                disabled={
                  busy ||
                  channelBusy ||
                  rampBusy ||
                  (actionModal === "resetCircuit" && !playbookConfirmed)
                }
                onClick={confirmAction}
              >
                {busy || channelBusy || rampBusy ? (
                  <Spinner size="sm" />
                ) : actionModal === "resetOwnerMfa" ? (
                  t("sa.tenants.detail.ownerMfa.confirmAction")
                ) : (
                  t("sa.tenants.detail.actions.confirm")
                )}
              </Button>
            )}
          </ModalFooter>
        </Modal>
      </Container>
    </div>
  );
};

export default SuperAdminTenantDetail;
